import { useId } from 'react';
import ReactModal from 'react-modal';
import { FiX } from 'react-icons/fi';

import { PersonAvatar } from 'components/person-avatar';
import Button from 'components/ui/button';
import { FOCO_SOBRE_AZUL, FOCO_VISIVEL } from 'utils/foco';
import { iniciaisDoFunci } from 'pages/Rede/feed-calculos';
import { MembroResumo, RealizacaoResumo } from 'services/minha-equipe.service';
import { CyclesInterface } from 'types/user';
import { useAppInerte } from 'utils/app-inerte';

import {
  faseDoCadastro,
  LinhaDaEquipe,
  numeroPtBR,
  rotuloDoAcordo,
  statusDaRealizacao,
} from '../minha-equipe-calculos';
import PilulaDeStatus from './PilulaDeStatus';

export interface PainelDoLideradoProps {
  /** Liderado aberto; null fecha o painel. */
  linha: LinhaDaEquipe | null;
  /** Dados do resumo do liderado (realizações e reconhecimentos por tipo); null = resumo indisponível. */
  membro: MembroResumo | null;
  /** `cycles` do AuthContext, para o mês de cadastro de cada realização. */
  cycles: CyclesInterface[] | undefined;
  /** Só na própria equipe (oculto na equipe lateral). */
  podeAlterarGestor: boolean;
  onFechar: () => void;
  onAlterarGestor: (linha: LinhaDaEquipe) => void;
}

/** Mesmo `#root` do `ModalFrame`: a aplicação fica oculta do leitor de tela com o painel aberto. */
const APP_ROOT_ID = 'root';

const SEM_REALIZACAO = 'Nenhuma realização cadastrada neste ciclo. Que tal lembrar a equipe do prazo de cadastramento?';

function Realizacao({ realizacao, cycles }: { realizacao: RealizacaoResumo; cycles: CyclesInterface[] | undefined }) {
  const fase = faseDoCadastro(realizacao.created, cycles);

  return (
    <li className="rounded-[10px] border border-solid border-surface-200 px-3.5 py-3">
      <p className="m-0 text-[12.5px] font-semibold leading-[1.45] text-content">{realizacao.title}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {/* Status fora da régua da fase (ex.: rascunho) fica numa pílula neutra. */}
        <PilulaDeStatus
          status={statusDaRealizacao(realizacao.status)}
          rotuloSemStatus="Em andamento"
          className="px-2.5 py-1 text-[9px]"
        />
        {fase && <span className="text-[10.5px] text-content-soft">{fase}</span>}
      </div>
    </li>
  );
}

/** Painel lateral do liderado (diálogo modal): números, realizações do ciclo e reconhecimentos por tipo. */
export default function PainelDoLiderado({
  linha,
  membro,
  cycles,
  podeAlterarGestor,
  onFechar,
  onAlterarGestor,
}: PainelDoLideradoProps) {
  const tituloId = useId();
  const acordo = rotuloDoAcordo(linha?.acordo);

  useAppInerte(!!linha);

  return (
    <ReactModal
      isOpen={!!linha}
      appElement={document.getElementById(APP_ROOT_ID) ?? undefined}
      onRequestClose={onFechar}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      role="dialog"
      aria={{ labelledby: tituloId }}
      portalClassName="relative z-50"
      overlayClassName="fixed inset-0 z-[100] bg-[rgba(0,45,75,0.45)]"
      className="fixed right-0 top-0 flex h-full w-[440px] max-w-full flex-col bg-brand-white shadow-[-18px_0_50px_rgba(17,24,39,.18)] outline-none"
    >
      {linha && (
        <>
          <div className="relative shrink-0 bg-brand-blue px-7 pb-[22px] pt-[26px] text-brand-white">
            <button
              type="button"
              onClick={onFechar}
              aria-label="Fechar detalhe"
              className={`absolute right-[18px] top-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-[rgba(255,255,255,0.18)] text-brand-white ${FOCO_SOBRE_AZUL}`}
            >
              <FiX size={15} strokeWidth={2.8} aria-hidden="true" />
            </button>
            <div className="flex items-center gap-3.5 pr-[38px]">
              <PersonAvatar
                image={linha.rosto.image}
                avatar={linha.rosto.avatar}
                avatarType={linha.rosto.avatarType}
                initials={iniciaisDoFunci(linha.nome)}
                size={56}
                initialsClassName="bg-brand-yellow font-display text-lg font-extrabold text-brand-blue-dark"
              />
              <div className="min-w-0">
                <h2 id={tituloId} className="m-0 font-display text-xl font-extrabold leading-[1.15] text-brand-yellow">
                  {linha.nome}
                </h2>
                {/* SPEC_DEVIATION: branco pleno (o design usa 92%, abaixo de 4,5:1 sobre o azul). */}
                <p className="m-0 mt-1 text-[11.5px]">
                  {linha.funcao} · {linha.chave}
                </p>
              </div>
            </div>
            {/* SPEC_DEVIATION: pílulas em `brand-blue-dark` (o branco a 15% do design deixa o texto abaixo de 4,5:1). */}
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-jj-pill bg-brand-blue-dark px-3 py-1.5 text-[10.5px] font-bold">{linha.diretoria}</span>
              {acordo && <span className="rounded-jj-pill bg-brand-blue-dark px-3 py-1.5 text-[10.5px] font-bold">{acordo}</span>}
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-7 pb-[26px] pt-[22px]">
            <dl className="m-0 grid grid-cols-3 gap-2.5">
              {[
                { rotulo: 'Pontos do ciclo', valor: linha.pontos },
                { rotulo: 'Selos', valor: linha.selos },
                { rotulo: 'Reconhecimentos', valor: linha.reconhecimentos },
              ].map(numero => (
                <div key={numero.rotulo} className="flex flex-col-reverse rounded-[10px] bg-surface-100 px-3.5 py-3">
                  {/* SPEC_DEVIATION: rótulo em `content` (o `text-soft` do design dá 4,43:1 no `surface-100`). */}
                  <dt className="mt-[5px] text-[10px] text-content">{numero.rotulo}</dt>
                  <dd className="m-0 font-display text-[19px] font-extrabold leading-none text-brand-blue">
                    {numeroPtBR(numero.valor)}
                  </dd>
                </div>
              ))}
            </dl>

            <section aria-labelledby={`${tituloId}-realizacoes`}>
              <h3 id={`${tituloId}-realizacoes`} className="m-0 mb-2.5 font-display text-sm font-bold text-brand-blue-dark">
                Realizações no ciclo
              </h3>
              {!membro ? (
                <p className="m-0 text-xs leading-[1.6] text-content-soft">Não foi possível carregar os números da equipe.</p>
              ) : membro.realizacoes.length ? (
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {membro.realizacoes.map(realizacao => (
                    <Realizacao key={realizacao._id} realizacao={realizacao} cycles={cycles} />
                  ))}
                </ul>
              ) : (
                <p className="m-0 text-xs leading-[1.6] text-content-soft [text-wrap:pretty]">{SEM_REALIZACAO}</p>
              )}
            </section>

            {membro && (
              <section aria-labelledby={`${tituloId}-reconhecimentos`}>
                <h3 id={`${tituloId}-reconhecimentos`} className="m-0 mb-2.5 font-display text-sm font-bold text-brand-blue-dark">
                  Reconhecimentos recebidos
                </h3>
                <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                  {[
                    { nome: '#VALEU', quantidade: membro.reconhecimentos.valeu },
                    { nome: '#MANDOUBEM', quantidade: membro.reconhecimentos.mandou },
                    { nome: '#UAU', quantidade: membro.reconhecimentos.uau },
                  ].map(tipo => (
                    <li
                      key={tipo.nome}
                      className="inline-flex items-center gap-[7px] rounded-jj-pill bg-surface-100 px-[13px] py-[7px] text-[11.5px] text-content"
                    >
                      <b className="font-display text-brand-blue-dark">{numeroPtBR(tipo.quantidade)}</b>
                      {tipo.nome}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="mt-auto flex flex-col gap-2.5">
              {podeAlterarGestor && (
                <Button variant="solido-destaque" size="sm" onClick={() => onAlterarGestor(linha)} className="w-full py-[13px]">
                  Alterar gestor
                </Button>
              )}
              <a
                href={`https://humanograma.intranet.bb.com.br/${linha.chave}`}
                target="_blank"
                rel="noreferrer"
                className={`box-border w-full rounded-jj-field border border-solid border-brand-blue bg-brand-white px-5 py-[13px] text-center text-[11.5px] font-bold uppercase tracking-[0.04em] text-brand-blue no-underline hover:bg-surface-100 hover:text-brand-blue-dark hover:no-underline focus:text-brand-blue ${FOCO_VISIVEL}`}
              >
                Ver no Humanograma<span className="sr-only"> (abre em nova aba)</span>
              </a>
            </div>
          </div>
        </>
      )}
    </ReactModal>
  );
}
